/**
 * @file ClawbackWorkspace.jsx
 * @module ui/src/components/admin/ClawbackWorkspace.jsx
 * @description
 *   C-70 — Clawback Actions Workspace.
 *   EXECUTE → Programs & Adjustments → Clawback Actions.
 *   Inline overlay pattern — no dedicated route (same as C-68, C-69).
 *
 *   S932 — Net-new component. Built per clawback_workspace_spec_c70_s931.md.
 *   Backend: churn_clawback_router.py (revived S932, AD-S931-02).
 *   AD-116: standalone runtime instrument — not through EP9 / plan amendment.
 *   AD-S931-02: Clawback policy CRUD stays in EP2b. This surface covers
 *               churn event ingestion and engine-calculated results only.
 *
 *   Tab 1 — Churn Events: event list + single ingest form + CSV batch upload.
 *   Tab 2 — Clawback Results: engine results by calculation run.
 *
 *   RBAC (enforced here):
 *     manager:              Results tab only (no Churn Events tab)
 *     comp_admin / finance: both tabs
 *
 *   No notification events (data pipeline, not approval workflow).
 *   No NN-10 SoD (no approval lifecycle on churn events).
 *
 * @author data-fit LLC · DecisionFlow · S932
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const API_BASE = '/api/v1';

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

function TabBar({ tabs, active, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid rgb(var(--color-border))' }}>
      {tabs.map(t => (
        <button key={t.key} type="button" onClick={() => onChange(t.key)}
          style={{
            fontSize: 13, fontWeight: 600, padding: '10px 18px',
            background: 'none', border: 'none', cursor: 'pointer',
            borderBottom: active === t.key ? '2px solid rgb(var(--color-primary))' : '2px solid transparent',
            color: active === t.key ? 'rgb(var(--color-primary))' : 'rgb(var(--color-text-muted))',
            marginBottom: -1,
          }}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

function SectionHeader({ title, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0 8px', borderBottom: '1px solid rgb(var(--color-border))', marginBottom: 12 }}>
      <span style={{ fontSize: 13, fontWeight: 700, color: 'rgb(var(--color-text))' }}>{title}</span>
      <div style={{ display: 'flex', gap: 8 }}>{children}</div>
    </div>
  );
}

function InlineError({ message }) {
  if (!message) return null;
  return (
    <div role="alert" style={{ fontSize: 12, color: 'rgb(var(--color-danger, 220 38 38))', padding: '8px 10px', borderRadius: 6, background: 'rgb(var(--color-danger, 220 38 38) / 0.07)', border: '1px solid rgb(var(--color-danger, 220 38 38) / 0.2)', marginBottom: 8 }}>
      {message}
    </div>
  );
}

function InlineSuccess({ message }) {
  if (!message) return null;
  return (
    <div role="status" style={{ fontSize: 12, color: 'rgb(var(--color-success, 22 163 74))', padding: '8px 10px', borderRadius: 6, background: 'rgb(var(--color-success, 22 163 74) / 0.07)', border: '1px solid rgb(var(--color-success, 22 163 74) / 0.2)', marginBottom: 8 }}>
      {message}
    </div>
  );
}

function StatusBadge({ status }) {
  const MAP = {
    loaded:  { cls: 'modifier-manager-badge--complete', label: 'Loaded' },
    partial: { cls: 'modifier-manager-badge--warning',  label: 'Partial' },
    failed:  { cls: 'modifier-manager-badge--not-created', label: 'Failed' },
  };
  const cfg = MAP[status] ?? { cls: '', label: status };
  return <span className={`modifier-manager-badge ${cfg.cls}`}>{cfg.label}</span>;
}

function CollapsibleForm({ title, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 8, marginBottom: 12, overflow: 'hidden' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '10px 14px', background: 'rgb(var(--color-surface-muted, var(--color-surface)))', border: 'none', cursor: 'pointer', borderBottom: open ? '1px solid rgb(var(--color-border))' : 'none' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'rgb(var(--color-text))' }}>{title}</span>
        <span style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && <div style={{ padding: '14px' }}>{children}</div>}
    </div>
  );
}

function FormField({ label, required, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: 'rgb(var(--color-text-muted))' }}>
        {label}{required && <span style={{ color: 'rgb(var(--color-danger, 220 38 38))', marginLeft: 2 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  border: '1px solid rgb(var(--color-border))', borderRadius: 6,
  padding: '6px 10px', fontSize: 12,
  background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))',
  width: '100%', boxSizing: 'border-box',
};

// ---------------------------------------------------------------------------
// Tab 1 — Churn Events
// ---------------------------------------------------------------------------

function ChurnEventsList({ refreshKey, onRefresh }) {
  const [items, setItems]   = useState([]);
  const [total, setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState(null);
  const [customerFilter, setCustomerFilter] = useState('');
  const [sourceFilter, setSourceFilter]     = useState('');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  useEffect(() => { setPage(0); }, [customerFilter, sourceFilter, refreshKey]);

  useEffect(() => {
    let active = true;
    setLoading(true); setError(null);
    const params = new URLSearchParams({ limit: PAGE_SIZE, offset: page * PAGE_SIZE });
    if (customerFilter) params.set('customer_identifier', customerFilter);
    if (sourceFilter)   params.set('source_system', sourceFilter);

    fetch(`${API_BASE}/churn-events?${params}`, { credentials: 'include' })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(data => {
        if (!active) return;
        setItems(data.churn_events ?? []);
        setTotal(data.total ?? 0);
      })
      .catch(e => { if (active) setError(e.message || 'Unable to load churn events.'); })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, [customerFilter, sourceFilter, page, refreshKey]);

  const startRow = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const endRow   = Math.min(page * PAGE_SIZE + PAGE_SIZE, total);

  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input type="text" placeholder="Filter by customer…" value={customerFilter}
          onChange={e => setCustomerFilter(e.target.value)}
          style={{ ...inputStyle, width: 200 }} />
        <input type="text" placeholder="Filter by source system…" value={sourceFilter}
          onChange={e => setSourceFilter(e.target.value)}
          style={{ ...inputStyle, width: 200 }} />
        {(customerFilter || sourceFilter) && (
          <button type="button" onClick={() => { setCustomerFilter(''); setSourceFilter(''); }}
            style={{ fontSize: 11, background: 'none', border: 'none', cursor: 'pointer', color: 'rgb(var(--color-text-muted))' }}>
            ✕ Clear
          </button>
        )}
        <button type="button" onClick={onRefresh}
          style={{ fontSize: 11, background: 'none', border: 'none', cursor: 'pointer', color: 'rgb(var(--color-text-muted))', marginLeft: 'auto' }}>
          ↻ Refresh
        </button>
      </div>

      {/* Row count */}
      <div style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))', marginBottom: 6 }}>
        {loading ? 'Loading…' : total === 0 ? 'No churn events found.' : `${startRow}–${endRow} of ${total.toLocaleString()} event${total !== 1 ? 's' : ''}`}
      </div>

      <InlineError message={error} />

      {/* Table */}
      <div className="modifier-manager-table-wrapper">
        <table className="modifier-manager-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>MRR</th>
              <th>Currency</th>
              <th>Churn Date</th>
              <th>Reason</th>
              <th>Source System</th>
              <th>Source Event ID</th>
              <th>Ingested At</th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 && (
              <tr><td colSpan={8} style={{ padding: '24px 16px', textAlign: 'center', color: 'rgb(var(--color-text-muted))', fontSize: 13 }}>Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} style={{ padding: '24px 16px', textAlign: 'center', color: 'rgb(var(--color-text-muted))', fontSize: 13 }}>
                No churn events found. Ingest an event or upload a CSV below.
              </td></tr>
            )}
            {items.map(ev => (
              <tr key={ev.churn_event_id}>
                <td style={{ fontSize: 12, fontWeight: 600 }}>{ev.customer_identifier}</td>
                <td style={{ fontSize: 12, fontFamily: 'ui-monospace,monospace' }}>{Number(ev.churned_mrr).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td style={{ fontSize: 12 }}>{ev.churn_currency_code}</td>
                <td style={{ fontSize: 12 }}>{ev.churn_date ? new Date(ev.churn_date).toLocaleDateString() : '—'}</td>
                <td style={{ fontSize: 12 }}>{ev.churn_reason_code ?? '—'}</td>
                <td style={{ fontSize: 12 }}>{ev.source_system}</td>
                <td style={{ fontSize: 11, fontFamily: 'ui-monospace,monospace', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.source_event_id}</td>
                <td style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>{ev.created_at ? new Date(ev.created_at).toLocaleString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, fontSize: 12 }}>
          <button type="button" className="secondary-button" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>← Prev</button>
          <span style={{ color: 'rgb(var(--color-text-muted))' }}>Page {page + 1}</span>
          <button type="button" className="secondary-button" onClick={() => setPage(p => p + 1)} disabled={endRow >= total}>Next →</button>
        </div>
      )}
    </div>
  );
}

function SingleIngestForm({ onSuccess }) {
  const EMPTY = { customer_identifier: '', churned_mrr: '', churn_currency_code: '', churn_date: '', source_system: '', source_event_id: '', churn_reason_code: '' };
  const [form, setForm]       = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]     = useState(null);
  const [success, setSuccess] = useState(null);

  function set(field) { return e => setForm(f => ({ ...f, [field]: e.target.value })); }

  async function handleSubmit() {
    setError(null); setSuccess(null);
    const required = ['customer_identifier', 'churned_mrr', 'churn_currency_code', 'churn_date', 'source_system', 'source_event_id'];
    for (const f of required) {
      if (!form[f]?.trim()) { setError(`${f.replace(/_/g, ' ')} is required.`); return; }
    }
    setSubmitting(true);
    try {
      const body = { ...form };
      if (!body.churn_reason_code?.trim()) delete body.churn_reason_code;
      const r = await fetch(`${API_BASE}/churn-events`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await r.json();
      if (!r.ok) {
        const msg = json?.error?.code === 'CHURN_EVENT_DUPLICATE'
          ? 'Event already ingested (source_system + source_event_id must be unique).'
          : json?.error?.message ?? `HTTP ${r.status}`;
        setError(msg); return;
      }
      setSuccess(`Churn event ingested: ${json.churn_event?.churn_event_id}`);
      setForm(EMPTY);
      onSuccess?.();
    } catch (e) {
      setError(e.message || 'Failed to ingest churn event.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <InlineError message={error} />
      <InlineSuccess message={success} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
        <FormField label="Customer Identifier" required>
          <input style={inputStyle} value={form.customer_identifier} onChange={set('customer_identifier')} placeholder="ACME Corp" />
        </FormField>
        <FormField label="Churned MRR" required>
          <input style={inputStyle} type="number" step="0.01" value={form.churned_mrr} onChange={set('churned_mrr')} placeholder="5000.00" />
        </FormField>
        <FormField label="Currency" required>
          <input style={inputStyle} value={form.churn_currency_code} onChange={set('churn_currency_code')} placeholder="USD" maxLength={3} />
        </FormField>
        <FormField label="Churn Date" required>
          <input style={inputStyle} type="date" value={form.churn_date} onChange={set('churn_date')} />
        </FormField>
        <FormField label="Source System" required>
          <input style={inputStyle} value={form.source_system} onChange={set('source_system')} placeholder="salesforce" />
        </FormField>
        <FormField label="Source Event ID" required>
          <input style={inputStyle} value={form.source_event_id} onChange={set('source_event_id')} placeholder="opp_001abc" />
        </FormField>
        <FormField label="Churn Reason Code">
          <input style={inputStyle} value={form.churn_reason_code} onChange={set('churn_reason_code')} placeholder="competitive_loss (optional)" />
        </FormField>
      </div>
      <div>
        <button type="button" className="primary-button" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Ingesting…' : 'Ingest Event'}
        </button>
      </div>
    </div>
  );
}

function CsvUploadForm({ onSuccess }) {
  const fileRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]   = useState(null);
  const [result, setResult] = useState(null);

  async function handleUpload() {
    setError(null); setResult(null);
    const file = fileRef.current?.files?.[0];
    if (!file) { setError('Select a CSV file first.'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_BASE}/churn-events/import`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const json = await r.json();
      if (!r.ok) { setError(json?.error?.message ?? `HTTP ${r.status}`); return; }
      setResult(json);
      if (fileRef.current) fileRef.current.value = '';
      if (json.rows_loaded > 0) onSuccess?.();
    } catch (e) {
      setError(e.message || 'Upload failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Required columns banner */}
      <div style={{ fontSize: 11, padding: '8px 10px', borderRadius: 6, background: 'rgb(var(--color-surface-muted, var(--color-surface)))', border: '1px solid rgb(var(--color-border))' }}>
        <span style={{ fontWeight: 700, color: 'rgb(var(--color-text))' }}>Required columns: </span>
        <code style={{ fontSize: 11, fontFamily: 'ui-monospace,monospace' }}>customer_identifier, churned_mrr, churn_currency_code, churn_date, source_system, source_event_id</code>
        <span style={{ color: 'rgb(var(--color-text-muted))' }}> · Optional: </span>
        <code style={{ fontSize: 11, fontFamily: 'ui-monospace,monospace' }}>churn_reason_code</code>
      </div>
      <InlineError message={error} />
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <input ref={fileRef} type="file" accept=".csv"
          style={{ fontSize: 12, color: 'rgb(var(--color-text))' }} />
        <button type="button" className="primary-button" onClick={handleUpload} disabled={submitting}>
          {submitting ? 'Uploading…' : 'Upload CSV'}
        </button>
      </div>
      {result && (
        <div style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 8, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <StatusBadge status={result.status} />
            <span style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))' }}>Import log: <code style={{ fontFamily: 'ui-monospace,monospace', fontSize: 11 }}>{result.import_log_id}</code></span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, auto)', gap: '6px 20px', fontSize: 12 }}>
            <span>Received</span><strong>{result.rows_received}</strong>
            <span>Loaded</span><strong style={{ color: result.rows_loaded > 0 ? 'rgb(var(--color-success, 22 163 74))' : undefined }}>{result.rows_loaded}</strong>
            <span>Rejected</span><strong style={{ color: result.rows_rejected > 0 ? 'rgb(var(--color-danger, 220 38 38))' : undefined }}>{result.rows_rejected}</strong>
            <span>Duplicates</span><strong>{result.duplicate_count}</strong>
          </div>
          {result.rejection_detail?.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'rgb(var(--color-text-muted))', marginBottom: 6 }}>Rejection detail (first {result.rejection_detail.length}):</div>
              <div className="modifier-manager-table-wrapper" style={{ maxHeight: 180, overflowY: 'auto' }}>
                <table className="modifier-manager-table">
                  <thead><tr><th>Row</th><th>Reason</th></tr></thead>
                  <tbody>
                    {result.rejection_detail.map((d, i) => (
                      <tr key={i}>
                        <td style={{ fontSize: 12 }}>{d.row}</td>
                        <td style={{ fontSize: 12, color: 'rgb(var(--color-danger, 220 38 38))' }}>{d.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ChurnEventsTab({ currentUser }) {
  const [refreshKey, setRefreshKey] = useState(0);
  const isWriter = currentUser?.user_type === 'comp_admin';

  function refresh() { setRefreshKey(k => k + 1); }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <SectionHeader title="Churn Events">
        <button type="button" className="secondary-button" onClick={refresh} style={{ fontSize: 11 }}>↻ Refresh</button>
      </SectionHeader>

      <ChurnEventsList refreshKey={refreshKey} onRefresh={refresh} />

      {isWriter && (
        <>
          <CollapsibleForm title="Ingest Single Event">
            <SingleIngestForm onSuccess={refresh} />
          </CollapsibleForm>
          <CollapsibleForm title="Upload CSV Batch">
            <CsvUploadForm onSuccess={refresh} />
          </CollapsibleForm>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 2 — Clawback Results
// ---------------------------------------------------------------------------

function ClawbackResultsTab({ fiscalPeriodId }) {
  const [runId, setRunId]   = useState('');
  const [posId, setPosId]   = useState('');
  const [items, setItems]   = useState([]);
  const [total, setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState(null);
  const [searched, setSearched] = useState(false);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  function handleSearch() {
    if (!runId.trim()) return;
    setPage(0);
    setSearched(true);
  }

  useEffect(() => {
    if (!searched || !runId.trim()) return;
    let active = true;
    setLoading(true); setError(null);
    const params = new URLSearchParams({ calculation_run_id: runId.trim(), limit: PAGE_SIZE, offset: page * PAGE_SIZE });
    if (posId.trim()) params.set('position_id', posId.trim());

    fetch(`${API_BASE}/churn-clawback-results?${params}`, { credentials: 'include' })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(data => {
        if (!active) return;
        setItems(data.churn_clawback_results ?? []);
        setTotal(data.total ?? 0);
      })
      .catch(e => { if (active) setError(e.message || 'Unable to load clawback results.'); })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, [searched, runId, posId, page]);

  const startRow = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const endRow   = Math.min(page * PAGE_SIZE + PAGE_SIZE, total);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <SectionHeader title="Clawback Results" />

      {/* Run ID picker */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <FormField label="Calculation Run ID" required>
          <input style={{ ...inputStyle, width: 340 }} value={runId}
            onChange={e => { setRunId(e.target.value); setSearched(false); }}
            placeholder="Enter calculation run UUID…"
            onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }} />
        </FormField>
        <FormField label="Position ID (optional)">
          <input style={{ ...inputStyle, width: 280 }} value={posId}
            onChange={e => { setPosId(e.target.value); setSearched(false); }}
            placeholder="Filter by position UUID…"
            onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }} />
        </FormField>
        <button type="button" className="primary-button" onClick={handleSearch} disabled={!runId.trim() || loading}>
          {loading ? 'Loading…' : 'Load Results'}
        </button>
      </div>

      {!searched && (
        <div style={{ padding: '32px 0', textAlign: 'center', color: 'rgb(var(--color-text-muted))', fontSize: 13 }}>
          Enter a calculation run ID to view clawback results.
        </div>
      )}

      {searched && (
        <>
          <InlineError message={error} />

          {!loading && !error && (
            <div style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>
              {total === 0
                ? 'No clawback results for this run. Verify the engine ran with active clawback policies.'
                : `${startRow}–${endRow} of ${total.toLocaleString()} result${total !== 1 ? 's' : ''}`}
            </div>
          )}

          {total > 0 && (
            <div className="modifier-manager-table-wrapper">
              <table className="modifier-manager-table">
                <thead>
                  <tr>
                    <th>Position</th>
                    <th>Customer</th>
                    <th>Churn Date</th>
                    <th>Churned MRR</th>
                    <th>Policy</th>
                    <th>Window (days)</th>
                    <th>Clawback %</th>
                    <th>Original Commission</th>
                    <th>Clawback Amount</th>
                    <th>Currency</th>
                    <th>Within Window</th>
                    <th>Days Since Close</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(r => (
                    <tr key={r.clawback_result_id}>
                      <td style={{ fontSize: 11, fontFamily: 'ui-monospace,monospace', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.position_id}</td>
                      <td style={{ fontSize: 12, fontWeight: 600 }}>{r.customer_identifier}</td>
                      <td style={{ fontSize: 12 }}>{r.churn_date ? new Date(r.churn_date).toLocaleDateString() : '—'}</td>
                      <td style={{ fontSize: 12, fontFamily: 'ui-monospace,monospace' }}>
                        {Number(r.churned_mrr).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td style={{ fontSize: 12 }}>{r.policy_name}</td>
                      <td style={{ fontSize: 12, textAlign: 'center' }}>{r.protection_window_days}</td>
                      <td style={{ fontSize: 12, textAlign: 'center' }}>{(Number(r.clawback_pct) * 100).toFixed(1)}%</td>
                      <td style={{ fontSize: 12, fontFamily: 'ui-monospace,monospace' }}>
                        {Number(r.original_commission).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td style={{ fontSize: 12, fontWeight: 700, fontFamily: 'ui-monospace,monospace', color: Number(r.clawback_amount) > 0 ? 'rgb(var(--color-danger, 220 38 38))' : undefined }}>
                        {Number(r.clawback_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td style={{ fontSize: 12 }}>{r.clawback_currency}</td>
                      <td style={{ fontSize: 12, textAlign: 'center' }}>
                        {r.within_window
                          ? <span className="modifier-manager-badge modifier-manager-badge--complete">Yes</span>
                          : <span className="modifier-manager-badge modifier-manager-badge--readonly">No</span>}
                      </td>
                      <td style={{ fontSize: 12, textAlign: 'center' }}>{r.days_since_close ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {total > PAGE_SIZE && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4, fontSize: 12 }}>
              <button type="button" className="secondary-button" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>← Prev</button>
              <span style={{ color: 'rgb(var(--color-text-muted))' }}>Page {page + 1}</span>
              <button type="button" className="secondary-button" onClick={() => setPage(p => p + 1)} disabled={endRow >= total}>Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ClawbackWorkspace (C-70) — main export
// ---------------------------------------------------------------------------

export default function ClawbackWorkspace({ fiscalPeriodId, currentUser, onClose }) {
  // RBAC: manager sees Results tab only
  const isManager  = currentUser?.user_type === 'manager';
  const canIngest  = currentUser?.user_type === 'comp_admin' || currentUser?.user_type === 'finance';

  const tabs = [
    ...(!isManager ? [{ key: 'events',  label: 'Churn Events' }] : []),
    { key: 'results', label: 'Clawback Results' },
  ];

  const [activeTab, setActiveTab] = useState(isManager ? 'results' : 'events');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, fontFamily: '"IBM Plex Sans","Segoe UI",Arial,sans-serif', color: 'rgb(var(--color-text))' }}>

      {/* Workspace header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgb(var(--color-text-muted))', margin: '0 0 4px' }}>
            EXECUTE · Programs &amp; Adjustments · Clawback Actions
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: 'rgb(var(--color-text))', margin: 0 }}>Clawback Actions</h1>
            <span className="modifier-manager-badge modifier-manager-badge--readonly">Standard</span>
          </div>
          <p style={{ fontSize: 13, color: 'rgb(var(--color-text-muted))', margin: '4px 0 0' }}>
            Churn event ingestion and engine-calculated clawback results.
            Policies are configured in the plan&apos;s Adjustments section.
          </p>
        </div>
        <button type="button" className="secondary-button" onClick={onClose}>← Back</button>
      </div>

      {/* Tab bar */}
      <TabBar tabs={tabs} active={activeTab} onChange={setActiveTab} />

      {/* Tab content */}
      <div style={{ paddingTop: 20 }}>
        {activeTab === 'events'  && <ChurnEventsTab currentUser={currentUser} />}
        {activeTab === 'results' && <ClawbackResultsTab fiscalPeriodId={fiscalPeriodId} />}
      </div>
    </div>
  );
}
